/**
 * Utility Helper Functions
 * 
 * This file provides common utility functions used throughout
 * the NovaQ Finance System.
 */

/**
 * Formats a date string to Mongolian locale format
 * 
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string
 * 
 * @example
 * ```typescript
 * const formatted = formatDate('2024-01-15');
 * // Returns: '2024-01-15'
 * 
 * const formatted = formatDate('2024-01-15', { year: 'numeric', month: 'long', day: 'numeric' });
 * // Returns: '2024 оны 1-р сарын 15'
 * ```
 */
export const formatDate = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }
): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('mn-MN', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Formats a date string to time format
 * 
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted time string
 * 
 * @example
 * ```typescript
 * const formatted = formatTime('2024-01-15T14:30:00');
 * // Returns: '14:30'
 * 
 * const formatted = formatTime('2024-01-15T14:30:00', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
 * // Returns: '14:30:00'
 * ```
 */
export const formatTime = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit'
  }
): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('mn-MN', options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return dateString;
  }
};

/**
 * Formats a number to currency format
 * 
 * @param amount - The number to format
 * @param currency - The currency code (default: 'MNT')
 * @returns Formatted currency string
 * 
 * @example
 * ```typescript
 * const formatted = formatCurrency(1500000);
 * // Returns: '1,500,000 ₮'
 * 
 * const formatted = formatCurrency(1500000, 'USD');
 * // Returns: '$1,500,000.00'
 * ```
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'MNT'
): string => {
  try {
    if (currency === 'MNT') {
      return `${amount.toLocaleString('mn-MN')} ₮`;
    }
    
    return new Intl.NumberFormat('mn-MN', {
      style: 'currency',
      currency: currency
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${amount} ${currency}`;
  }
};

/**
 * Formats a number with thousand separators
 * 
 * @param num - The number to format
 * @returns Formatted number string
 * 
 * @example
 * ```typescript
 * const formatted = formatNumber(1234567);
 * // Returns: '1,234,567'
 * ```
 */
export const formatNumber = (num: number): string => {
  try {
    return num.toLocaleString('mn-MN');
  } catch (error) {
    console.error('Error formatting number:', error);
    return num.toString();
  }
};

/**
 * Formats a number to salary format with thousand separators only
 * 
 * @param value - The number or string to format
 * @returns Formatted salary string (#,###)
 * 
 * @example
 * ```typescript
 * const formatted = formatSalary(1234567.89);
 * // Returns: '1,234,568'
 * 
 * const formatted = formatSalary('1234567');
 * // Returns: '1,234,567'
 * ```
 */
export const formatSalary = (value: number | string | null | undefined): string => {
  try {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) {
      return '';
    }
    
    // Format with thousand separators only (no decimal places)
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  } catch (error) {
    console.error('Error formatting salary:', error);
    return value?.toString() || '';
  }
};

/**
 * Parses a formatted salary string back to number
 * 
 * @param formattedValue - The formatted string (e.g., "1,234,567.89")
 * @returns Parsed number
 * 
 * @example
 * ```typescript
 * const parsed = parseSalary('1,234,567.89');
 * // Returns: 1234567.89
 * ```
 */
export const parseSalary = (formattedValue: string): number | null => {
  try {
    if (!formattedValue || formattedValue.trim() === '') {
      return null;
    }
    
    // Remove thousand separators and parse
    const cleanValue = formattedValue.replace(/,/g, '');
    const parsed = parseFloat(cleanValue);
    
    return isNaN(parsed) ? null : parsed;
  } catch (error) {
    console.error('Error parsing salary:', error);
    return null;
  }
};

/**
 * Formats salary for input with cursor position management
 * 
 * @param value - The numeric value
 * @param displayValue - The current display value in input
 * @param cursorPosition - Current cursor position
 * @returns Object with formatted value and new cursor position
 * 
 * @example
 * ```typescript
 * const result = formatSalaryForInput(1234567.89, "1234567.8", 10);
 * // Returns: { value: "1,234,567.89", cursorPosition: 12 }
 * ```
 */
export const formatSalaryForInput = (
  value: number | null, 
  displayValue: string, 
  cursorPosition: number
): { value: string; cursorPosition: number } => {
  try {
    if (value === null || value === undefined) {
      return { value: '', cursorPosition: 0 };
    }

    // Count commas before cursor position in original display value
    const commasBeforeCursor = (displayValue.substring(0, cursorPosition).match(/,/g) || []).length;
    
    // Format the number
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Count commas in formatted value
    const totalCommas = (formatted.match(/,/g) || []).length;
    
    // Adjust cursor position based on comma changes
    const commaDifference = totalCommas - commasBeforeCursor;
    const newCursorPosition = Math.max(0, cursorPosition + commaDifference);
    
    return { value: formatted, cursorPosition: newCursorPosition };
  } catch (error) {
    console.error('Error formatting salary for input:', error);
    return { value: displayValue, cursorPosition: cursorPosition };
  }
};

/**
 * Capitalizes the first letter of a string
 * 
 * @param str - The string to capitalize
 * @returns Capitalized string
 * 
 * @example
 * ```typescript
 * const capitalized = capitalize('hello world');
 * // Returns: 'Hello world'
 * ```
 */
export const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Converts a string to title case
 * 
 * @param str - The string to convert
 * @returns Title case string
 * 
 * @example
 * ```typescript
 * const titleCase = toTitleCase('hello world');
 * // Returns: 'Hello World'
 * ```
 */
export const toTitleCase = (str: string): string => {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Truncates a string to a specified length
 * 
 * @param str - The string to truncate
 * @param length - Maximum length
 * @param suffix - Suffix to add when truncated (default: '...')
 * @returns Truncated string
 * 
 * @example
 * ```typescript
 * const truncated = truncate('This is a long string', 10);
 * // Returns: 'This is a ...'
 * ```
 */
export const truncate = (str: string, length: number, suffix: string = '...'): string => {
  if (!str || str.length <= length) return str;
  return str.substring(0, length) + suffix;
};

/**
 * Generates a random string of specified length
 * 
 * @param length - Length of the random string
 * @returns Random string
 * 
 * @example
 * ```typescript
 * const random = generateRandomString(8);
 * // Returns: 'aB3dE7fG'
 * ```
 */
export const generateRandomString = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generates a UUID v4
 * 
 * @returns UUID string
 * 
 * @example
 * ```typescript
 * const uuid = generateUUID();
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 * ```
 */
export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Deep clones an object
 * 
 * @param obj - The object to clone
 * @returns Cloned object
 * 
 * @example
 * ```typescript
 * const original = { a: 1, b: { c: 2 } };
 * const cloned = deepClone(original);
 * // Returns: { a: 1, b: { c: 2 } } (different reference)
 * ```
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

/**
 * Debounces a function call
 * 
 * @param func - The function to debounce
 * @param wait - Wait time in milliseconds
 * @param immediate - Whether to call immediately
 * @returns Debounced function
 * 
 * @example
 * ```typescript
 * const debouncedSearch = debounce((query) => {
 *   console.log('Searching for:', query);
 * }, 300);
 * 
 * debouncedSearch('test'); // Will only execute after 300ms of no calls
 * ```
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    
    const callNow = immediate && !timeout;
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func(...args);
  };
};

/**
 * Throttles a function call
 * 
 * @param func - The function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 * 
 * @example
 * ```typescript
 * const throttledScroll = throttle(() => {
 *   console.log('Scrolling');
 * }, 100);
 * 
 * // Will only execute once per 100ms
 * throttledScroll();
 * ```
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, empty object)
 * 
 * @param value - The value to check
 * @returns True if empty, false otherwise
 * 
 * @example
 * ```typescript
 * isEmpty(''); // true
 * isEmpty(null); // true
 * isEmpty([]); // true
 * isEmpty({}); // true
 * isEmpty('hello'); // false
 * isEmpty([1, 2]); // false
 * ```
 */
export const isEmpty = (value: any): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Gets a nested property value safely
 * 
 * @param obj - The object to get the property from
 * @param path - The property path (e.g., 'user.profile.name')
 * @param defaultValue - Default value if property doesn't exist
 * @returns The property value or default value
 * 
 * @example
 * ```typescript
 * const user = { profile: { name: 'John' } };
 * const name = getNestedProperty(user, 'profile.name', 'Unknown');
 * // Returns: 'John'
 * 
 * const age = getNestedProperty(user, 'profile.age', 0);
 * // Returns: 0 (default value)
 * ```
 */
export const getNestedProperty = (
  obj: any,
  path: string,
  defaultValue: any = undefined
): any => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : defaultValue;
};

/**
 * Sets a nested property value safely
 * 
 * @param obj - The object to set the property on
 * @param path - The property path (e.g., 'user.profile.name')
 * @param value - The value to set
 * @returns The modified object
 * 
 * @example
 * ```typescript
 * const user = {};
 * setNestedProperty(user, 'profile.name', 'John');
 * // Returns: { profile: { name: 'John' } }
 * ```
 */
export const setNestedProperty = (obj: any, path: string, value: any): any => {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  let current = obj;
  
  for (const key of keys) {
    if (current[key] == null || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[lastKey] = value;
  return obj;
};

/**
 * Removes duplicate values from an array
 * 
 * @param array - The array to deduplicate
 * @param key - Optional key to deduplicate by (for objects)
 * @returns Array with duplicates removed
 * 
 * @example
 * ```typescript
 * const numbers = [1, 2, 2, 3, 3, 3];
 * const unique = uniqueArray(numbers);
 * // Returns: [1, 2, 3]
 * 
 * const users = [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }, { id: 1, name: 'John' }];
 * const uniqueUsers = uniqueArray(users, 'id');
 * // Returns: [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
 * ```
 */
export const uniqueArray = <T>(array: T[], key?: keyof T): T[] => {
  if (!key) {
    return [...new Set(array)];
  }
  
  const seen = new Set();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Groups array items by a specified key
 * 
 * @param array - The array to group
 * @param key - The key to group by
 * @returns Object with grouped items
 * 
 * @example
 * ```typescript
 * const users = [
 *   { role: 'admin', name: 'John' },
 *   { role: 'user', name: 'Jane' },
 *   { role: 'admin', name: 'Bob' }
 * ];
 * const grouped = groupBy(users, 'role');
 * // Returns: { admin: [{ role: 'admin', name: 'John' }, { role: 'admin', name: 'Bob' }], user: [{ role: 'user', name: 'Jane' }] }
 * ```
 */
export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const group = String(item[key]);
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
    return groups;
  }, {} as Record<string, T[]>);
};

/**
 * Sorts an array by a specified key
 * 
 * @param array - The array to sort
 * @param key - The key to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns Sorted array
 * 
 * @example
 * ```typescript
 * const users = [{ name: 'John', age: 30 }, { name: 'Jane', age: 25 }];
 * const sorted = sortBy(users, 'age', 'asc');
 * // Returns: [{ name: 'Jane', age: 25 }, { name: 'John', age: 30 }]
 * ```
 */
export const sortBy = <T>(
  array: T[],
  key: keyof T,
  direction: 'asc' | 'desc' = 'asc'
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

/**
 * Converts bytes to human readable format
 * 
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places
 * @returns Human readable string
 * 
 * @example
 * ```typescript
 * const readable = formatBytes(1024);
 * // Returns: '1 KB'
 * 
 * const readable = formatBytes(1048576);
 * // Returns: '1 MB'
 * ```
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Calculates the difference between two dates
 * 
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Object with time differences
 * 
 * @example
 * ```typescript
 * const date1 = new Date('2024-01-01');
 * const date2 = new Date('2024-01-15');
 * const diff = getDateDifference(date1, date2);
 * // Returns: { days: 14, hours: 336, minutes: 20160, seconds: 1209600 }
 * ```
 */
export const getDateDifference = (date1: Date, date2: Date) => {
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());
  
  return {
    milliseconds: diffInMs,
    seconds: Math.floor(diffInMs / 1000),
    minutes: Math.floor(diffInMs / (1000 * 60)),
    hours: Math.floor(diffInMs / (1000 * 60 * 60)),
    days: Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  };
};
